/**
 * Represents a collection of values that can be looked up by a string or number key.
 */
export class Dictionary<T> {
    /**
     * Initializes a new instance of the @see:Dictionary<T> class with an optional collection of values.
     * @param values An optional array of values to initially populate the dictionary with.
     * @param keySelector A function that can select a key from each value in the specified array.
     */
    constructor(values?: T[], keySelector: (value: T) => string | number = value => (<any>value).toString()) {
        if (values) {
            for (var i = 0, count = values.length; i < count; ++i) {
                var value = values[i]
                this._values[keySelector(value).toString()] = value
            }
            this._count = values.length
        }
    }
    private _values: { [key: string]: T } = {}
    private _count = 0

    /**
     * Gets the number of entries in the dictionary.
     */
    public get count(): number {
        return this._count
    }
    /**
     * Returns a new array object containing the keys (as their string representations) in the dictionary.
     */
    public keys(): string[] {
        var keys: any[] = []
        for (var key in this._values) {
            if (this._values.hasOwnProperty(key)) {
                keys.push(key)
            }
        }
        return keys
    }
    /**
     * Returns a new array object containing the values in the dictionary.
     */
    public values(): T[] {
        var values: any[] = []
        for (var key in this._values) {
            if (this._values.hasOwnProperty(key)) {
                values.push(this._values[key])
            }
        }
        return values
    }
    /**
     * Gets the value associated with the specified key. If no entry with the key exists, the specified default value is returned.
     * @param key The key of the value to get.
     * @param defaultValue The value to return if no entry with the specified key exists. If not specified, undefined is used.
     */
    public get(key: string | number, defaultValue?: T): T | undefined {
        if (key == null || key == undefined) return defaultValue
        var value = this._values[key.toString()]
        return value != undefined ? value : defaultValue
    }
    /**
     * Gets the value associated with the specified key. If no entry with the key yet exists, a new value is created, added to the dictionary,
     * and returned.
     * @param key The key of the value to get.
     * @param createValue A function that can create a new value.
     */
    public getOrAdd(key: string | number, createValue: () => T): T | undefined {
        if (key == null || key == undefined) return undefined
        var value = this._values[key.toString()]
        if (value == undefined) {
            this._values[key.toString()] = value = createValue()
            ++this._count
        }
        return value
    }
    /**
     * Adds an entry with the specified key and value to the dictionary, returning a value indicating whether the entry was successfully added
     * (i.e., the key did not already exist).
     * @param key The key of the entry to add.
     * @param value The value of the entry to add.
     */
    public add(key: string | number, value: T): boolean {
        if (key == null || key == undefined) return false
        if (!this._values.hasOwnProperty(key.toString())) {
            this._values[key.toString()] = value
            ++this._count
            return true
        }
        return false
    }
    /**
     * Adds an entry with the specified key and value to the dictionary if the key does not already exists, or replaces the value if the key
     * does already exist, returning the value specified.
     * @param key The key of the entry to set.
     * @param value The value of the entry to set.
     */
    public set(key: string | number, value: T): T | undefined {
        if (key == null || key == undefined) return undefined
        if (!this.add(key, value)) {
            this._values[key.toString()] = value
        }
        return value
    }
    /**
     * Removes the entry with the specified key from the dictionary, returning a value indicating whether the entry was successfully removed.
     * @param key The key of the entry to remove.
     */
    public remove(key: string | number): boolean {
        if (key == null || key == undefined) return false
        if (this._values.hasOwnProperty(key.toString())) {
            delete this._values[key.toString()]
            --this._count
            return true
        }
        return false
    }
    /**
     * Determines whether the dictionary contains an entry with the specified key.
     * @param key The key to locate in the dictionary.
     */
    public hasKey(key: string | number): boolean {
        if (key == null || key == undefined) return false
        return this._values.hasOwnProperty(key.toString())
    }
    /**
     * Performs a linear search to determine whether the dictionary contains any entries with the specified value.
     * @param key The value to locate in the dictionary.
     */
    public hasValue(value: T): boolean {
        for (var key in this._values) {
            if (this._values.hasOwnProperty(key) && this._values[key] == value) {
                return true
            }
        }
        return false
    }
    /**
     * Performs the specified action for each entry in the dictionary.
     * @param action A function to call for each key (as its string representation) and value entry in the dictionary.
     */
    public forEach(action: (key: string, value: T) => void): void {
        for (var key in this._values) {
            action(key, this._values[key])
        }
    }
    /**
     * Removes all entries from the dictionary.
     */
    public clear(): void {
        this._values = {}
        this._count = 0
    }
}
